<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { apiQuery } from "@/api/j24api";
// import { Auth } from "aws-amplify";
import { fetchAuthSession } from "aws-amplify/auth";

let collectionObj = reactive({
  existingCollectionId: "",
  collectionName: "",
  collectionDescription: "",
});

const currentStep = ref(1);

const showWarningDialog = ref(false);
const showDeleteDialog = ref(false);
const showDeleteSuccessDialog = ref(false);
const showDeleteFailedDialog = ref(false);
const showCompletionDialog = ref(false);
const showCompletionWarningDialog = ref(false);
const showSubmissionDialog = ref(false);

var search = ref("");

const expanded = ref([]);

var loading = ref(false);

var itemArray = ref([]);
var collectionsArray = ref([]);

var showCreationDialog = ref(false);

var selected = ref([]);
var selectedArray = ref([]);

var selectedCollection = ref([]);
var selectedCollectionArray = ref([]);

var selectedScope = ref(["All"]);
var selectedType = ref(["All"]);

var scopeOptions = ref([
  "All",
  "Intune",
  "Windows",
  "macOS",
  "iOS-iPadOS",
  "Android",
  "Linux",
]);

var typeOptions = ref([
  "All",
  "AppCategories",
  "Applications",
  "AppProtectionPolicies",
  "AssignmentFilters",
  "DeploymentProfiles",
  "DeviceCompliance",
  "DeviceConfiguration",
  "DeviceEnrolmentConfigurations",
  "EndpointSecurity",
  "GroupPolicyConfiguration",
  "Scripts",
  "SettingsCategories",
]);

const steps = [
  { title: "Select Collection", value: 1, complete: false },
  { title: "Select Items", value: 2, complete: false },
  { title: "Review and Submit", value: 3, complete: false },
];

const itemHeaders = [
  { title: "Name", value: "name", sortable: true, width: "60%" },
  { title: "Type", value: "type", sortable: true, width: "30%" },
  { title: "Scope", value: "scope", sortable: true, width: "10%" },
];

const collectionHeaders = [
  { title: "Name", value: "name", sortable: true },
  { title: "Description", value: "description", sortable: false },
  {
    title: "Created/Last Edited By",
    value: "latestEditor",
    sortable: true,
  },
];

const deployHeaders = [
  { title: "Name", value: "name", width: "60%" },
  { title: "Type", value: "type", width: "15%" },
  { title: "Scope", value: "scope", width: "10%" },
  { title: "Status", value: "status", width: "10%" },
];

const itemsPerPage = [
  { value: 25, title: "25" },
  { value: 50, title: "50" },
  { value: 100, title: "100" },
  { value: -1, title: "All" },
];

onMounted(async () => {
  await getData();
});

async function getData() {
  loading.value = true;

  let items = await apiQuery("ELA", "mdmTools/command", {
    mdm: "intune",
    command: "getItems",
  });

  items.forEach((item) => {
    /* Add a unique id to each item, this is to be the index for transitions between pages.
    unfortuantely this was the only way to acheive this as the MongoDB _id is not usable for this purpose. */
    item.id = Math.floor(Math.random() * 10000000000000000000);
    itemArray.value.push(item);
  });

  let collections = await apiQuery("ELA", "mdmTools/command", {
    mdm: "intune",
    command: "getCollections",
  });

  collections.forEach((collection) => {
    // Same again for collections.
    collection.id = Math.floor(Math.random() * 10000000000000000000);
    collectionsArray.value.push(collection);
  });

  loading.value = false;
}

function clearCollectionObject() {
  collectionObj.existingCollectionId = "";
  collectionObj.collectionName = "";
  collectionObj.collectionDescription = "null";
}

function nextStep() {
  if (currentStep.value < steps.length) {
    // Mark the current step as complete.
    steps[currentStep.value - 1].complete = true;

    switch (currentStep.value) {
      case 1:
        if (currentStep.value == 1 && selectedCollection.value.length == 0) {
          // If the user has not selected a collection, show the warning dialog box.
          selectionWarningDialogBox(true);
        } else {
          // If the user has selected a collection from the collections page, add the contained items to the selected items array.
          getCollectionItemsDetails.value;

          // Get the name of the selected collection for the page header.
          getCollectionDetails();

          // Match the selected items from the collections page with the items from the config page, then preselect them.
          matchCollectionIds(selectedCollectionArray, itemArray);

          // Move to the next step.
          currentStep.value++;
        }
        break;
      case 2:
        if (
          currentStep.value == 2 &&
          getSelectedItemsDetails.value.length == 0
        ) {
          // If the user has not selected any items, show the warning dialog box.
          selectionWarningDialogBox(true);
        } else {
          // Move to the next step.
          currentStep.value++;
        }
        break;
      default:
        // Do nothing
        break;
    }
  }
}

function getCollectionDetails() {
  if (selectedCollection.value.length > 0) {
    var id = selectedCollection.value[0];

    const selectedItem = collectionsArray.value.find((item) => item.id === id);
    collectionObj.collectionName = selectedItem.name;
    collectionObj.collectionDescription = selectedItem.description;
    collectionObj.existingCollectionId = selectedItem._id;
  }
}

function prevStep() {
  if (currentStep.value > 1) {
    currentStep.value--;
    // Mark the current step as incomplete if they go back.
    steps[currentStep.value - 1].complete = false;
  }
  // Clear the collection name and selected items if the user goes back to the first page.
  if (currentStep.value == 1) {
    clearCollectionObject();
    selected.value = [];
    selectedCollection.value = [];
    selectedCollectionArray.value = [];
  }
}

const getSelectedItemsDetails = computed(() => {
  const uniqueItemIds = new Set();
  return selected.value
    .map((id) => {
      /* When transitioning between steps, only the id of the selected items are passed,
      this function references the id and collects the rest of the missing data for the new page
      It also removes duplicate items just in case the user switches back and forth between the pages. */
      const selectedItem = itemArray.value.find((item) => item.id === id);
      if (selectedItem && !uniqueItemIds.has(id)) {
        uniqueItemIds.add(id);
        const { _id, name, description, scope, type } = selectedItem;
        const newItem = {
          _id,
          name,
          description,
          scope,
          type,
        };
        return newItem;
      }
    })
    .filter(Boolean);
});

const getCollectionItemsDetails = computed(() =>
  selectedCollection.value
    .map((id) => {
      /* When transitioning between steps, only the id of the selected items are passed,
      this function references the id and collects the reset of the missing data for the new page */
      const selectedItem = collectionsArray.value.find(
        (item) => item.id === id
      );
      if (selectedItem) {
        for (const key in selectedItem.items) {
          selectedItem.items[key].forEach((item) => {
            selectedCollectionArray.value.push(item);
          });
        }
      }
    })
    .filter(Boolean)
);

const filteredItems = computed(() => {
  // If both selectedScope and selectedType are set to 'All', return all items.
  if (selectedScope.value[0] === "All" && selectedType.value[0] === "All") {
    return itemArray.value;
  }

  // Otherwise, filter based on selectedScope and selectedType.
  return itemArray.value.filter((item) => {
    const passesScope =
      selectedScope.value[0] === "All" ||
      selectedScope.value.includes(item.scope);
    const passesType =
      selectedType.value[0] === "All" || selectedType.value.includes(item.type);

    return passesScope && passesType;
  });
});

function processSelectedItems() {
  // First we need to get the selected items from the collections page, then compare them to the items from the config page.
  selectedArray.value = getSelectedItemsDetails.value;

  // If the user is creating a new collection, we don't need to compare the items, just add them to the array. Else process the comparisons
  if (selectedCollection.value.length == 0) {
    for (const item of selectedArray.value) {
      item.status = "Add";
    }
    return selectedArray.value;
  } else {
    /* This section compares the selected items from the originally selected collection, then compares them to the items that are currently selected.
    It then adds the status of "No change", "Add" or "Remove" to each item, then returns the array for the review page. */
    const originalIds = [];
    var processedArray = [];

    var id = selectedCollection.value[0];
    const selectedItem = collectionsArray.value.find((item) => item.id === id);

    for (const key in selectedItem.items) {
      selectedItem.items[key].forEach((item) => {
        originalIds.push({ ...item, type: key });
      });
    }

    // We need to find the items that are in the originally selected collection, but have been de-selected, then re-add them to the proccessedArray.
    originalIds.forEach((originalItem) => {
      const foundItem = selectedArray.value.find(
        (selectedItem) => selectedItem._id === originalItem._id
      );

      if (!foundItem) {
        originalItem.status = "Remove";
        processedArray.push(originalItem);
      }
    });

    // We need to find the items that are in the originally selected collection, but not currently selected, or add new items to the proccessedArray.
    selectedArray.value.forEach((selectedItem) => {
      const foundItem = originalIds.find(
        (originalItem) => originalItem._id === selectedItem._id
      );

      if (foundItem) {
        selectedItem.status = "No change";
        processedArray.push(selectedItem);
      } else {
        selectedItem.status = "Add";
        processedArray.unshift(selectedItem);
      }
    });

    return processedArray;
  }
}

function changeStatusColour(status) {
  switch (status) {
    case "No change":
      return "grey";
    case "Add":
      return "green";
    case "Remove":
      return "red";
    default:
      return "grey";
  }
}

function creationDialogBox(status) {
  if (status == true) {
    showCreationDialog.value = true;
  } else {
    showCreationDialog.value = false;
  }
}

function selectionWarningDialogBox(status) {
  if (status == true) {
    showWarningDialog.value = true;
  } else {
    showWarningDialog.value = false;
  }
}

function deleteDialogBox(status) {
  if (status == true) {
    showDeleteDialog.value = true;
  } else {
    showDeleteDialog.value = false;
  }
}

function deleteSuccessDialogBox(status) {
  if (status == true) {
    showDeleteSuccessDialog.value = true;
  } else {
    showDeleteSuccessDialog.value = false;
  }
}

function deleteFailedDialogBox(status) {
  if (status == true) {
    showDeleteFailedDialog.value = true;
  } else {
    showDeleteFailedDialog.value = false;
  }
}

function completionDialogBox(status) {
  if (status == true) {
    showCompletionDialog.value = true;
  } else {
    showCompletionDialog.value = false;
  }
}

function submissionDialogBox(status) {
  if (status == true) {
    showSubmissionDialog.value = true;
  } else {
    showSubmissionDialog.value = false;
  }
}

function completionWarningDialogBox(status) {
  if (status == true) {
    showCompletionWarningDialog.value = true;
  } else {
    showCompletionWarningDialog.value = false;
  }
}

function createCollectionDialogBox(status) {
  if (status == true) {
    showCreationDialog.value = true;
  } else {
    showCreationDialog.value = false;
  }
}

function matchCollectionIds(selectedCollectionArray, itemArray) {
  /* This function matches the selected items from the collections page with the items from the config page, then preselects them.
  Items are matched by their MongoDB _id (string), but v-data-table uses id (integer);
  This will match the items by their _id, then pushes the temporary generated id for that item to the selected array, allowing it to be preselected.
  */
  selectedCollectionArray.value.forEach((selectedItem) => {
    const matchingConfigItem = itemArray.value.find(
      (configItem) => configItem._id === selectedItem._id
    );
    if (matchingConfigItem) {
      selected.value.push(matchingConfigItem.id);
    }
  });
}

async function createNewCollection() {
  currentStep.value++;
  showCreationDialog.value = false;
}

async function sendLog(logType) {
  var portalUser = await getPortalUser();

  var afterArray = [];
  var beforeArray = [];
  var log = {};
  var logObject = {};
  var type;

  // Get the before and after items from their respective arrays.
  getCollectionItemsDetails.value;
  getSelectedItemsDetails.value;

  // Build the before and after objects/arrays.
  for (const b of selectedCollectionArray.value) {
    beforeArray.push({
      _id: b._id,
      name: b.name,
      type: b.type,
      scope: b.scope,
    });
  }

  for (const a of getSelectedItemsDetails.value) {
    afterArray.push({
      _id: a._id,
      name: a.name,
      type: a.type,
      scope: a.scope,
    });
  }

  // Check if the user is creating a new collection or editing an existing one.
  switch (logType) {
    case "edit":
      if (collectionObj.existingCollectionId) {
        type = "Update";

        // Build the log object.
        logObject = {
          mdm: "Intune",
          logType: "CollectionEditor",
          portalUser: portalUser,
          timestamp: new Date().toISOString(),
          log: (log = {
            collectionId: collectionObj.existingCollectionId,
            collectionName: collectionObj.collectionName,
            collectionState: type,
            beforeItems: beforeArray,
            afterItems: afterArray,
          }),
        };
      } else {
        type = "Create";

        // Build the log object.
        logObject = {
          mdm: "Intune",
          logType: "CollectionEditor",
          portalUser: portalUser,
          timestamp: new Date().toISOString(),
          log: (log = {
            collectionId: collectionObj.existingCollectionId,
            collectionName: collectionObj.collectionName,
            collectionState: type,
            items: afterArray,
          }),
        };
      }

      break;
    case "delete":
      type = "Delete";

      // Build the log object.
      logObject = {
        mdm: "Intune",
        logType: "CollectionEditor",
        portalUser: portalUser,
        timestamp: new Date().toISOString(),
        log: (log = {
          collectionId: collectionObj.existingCollectionId,
          collectionName: collectionObj.collectionName,
          collectionState: type,
          items: beforeArray,
        }),
      };

      break;
    default:
      // Do nothing
      break;
  }

  // Send the log object to the API.
  await apiQuery("ELA", "mdmTools/command", {
    mdm: "intune",
    command: "log",
    payload: logObject,
  });
}

async function getPortalUser() {
  // Get the ELA authenticated user's email address.
  const currentAuthenticatedUser = await fetchAuthSession();
  if (currentAuthenticatedUser) {
    var email = currentAuthenticatedUser.tokens.idToken.payload.email;
  }

  return email;
}

async function deleteCollection(collection) {
  // Close the delete dialog box.
  showDeleteDialog.value = false;

  // Get the index, MongoDB _id and name of the selected collection, the latter two are used for the log submission.
  var id = collection[0];
  const selectedItem = collectionsArray.value.find((item) => item.id === id);
  collectionObj.existingCollectionId = selectedItem._id;
  collectionObj.collectionName = selectedItem.name;

  // Send the id to the API to delete the collection.
  try {
    const result = await apiQuery("ELA", "mdmTools/command", {
      mdm: "intune",
      command: "deleteCollection",
      payload: selectedItem,
    });

    if (result == "Success") {
      // Remove the item from the collectionsArray and UI.
      const index = collectionsArray.value.indexOf(selectedItem);
      if (index > -1) {
        collectionsArray.value.splice(index, 1);
      }
      showDeleteSuccessDialog.value = true;

      // Send the log to the API.
      await sendLog("delete");
    } else {
      showDeleteFailedDialog.value = true;
    }
  } catch (error) {
    console.log(error);
    showDeleteFailedDialog.value = true;
  }

  // Clear the previously seleted collection, variables and reset the button.
  clearCollectionObject();
  selected.value = [];
  selectedCollection.value = [];
  selectedCollectionArray.value = [];
}

function resetWizard() {
  // Following the successful submission of a collection, reset the wizard.

  // Close the completion dialog box.
  completionDialogBox(false);
  // Set the page to 1
  currentStep.value = 1;
  // Clear any loaded collections and items so they can be reloaded.
  collectionsArray.value = [];
  itemArray.value = [];
  // Set any completed steps to false (for the v-stepper-header).
  steps.forEach((step) => {
    step.complete = false;
  });

  // Clear the collection object.
  clearCollectionObject();

  // Clear other values
  search.value = "";
  selected.value = [];
  selectedArray.value = [];
  // Reload new items and collections.
  getData();
}

async function submitCollection() {
  // Close the submission dialog box.
  showSubmissionDialog.value = false;

  var portalUser = await getPortalUser();

  var items = [];
  var collectionPayload = {};
  var type = "";

  // Check if the user is creating a new collection or editing an existing one.
  if (collectionObj.existingCollectionId) {
    type = "existing";
  } else {
    type = "new";
  }

  // Filter out any items labelled for removal.
  for (const item of selectedArray.value) {
    if (item.status != "Remove") {
      items.push(item._id);
    }
  }

  // Build the payload for the API call.
  collectionPayload = {
    _id: collectionObj.existingCollectionId,
    name: collectionObj.collectionName,
    description: collectionObj.collectionDescription,
    latestEditor: portalUser,
    type: type,
    items: items,
  };

  try {
    // Send the payload to the API.
    let result = await apiQuery("ELA", "mdmTools/command", {
      mdm: "intune",
      command: "editCollection",
      payload: collectionPayload,
    });

    if (result == "Success") {
      // Send the log to the API.
      await sendLog("edit");

      completionDialogBox(true);
    } else {
      completionWarningDialogBox(true);
    }
  } catch (error) {
    console.log(error);
    completionWarningDialogBox(true);
  }
}
</script>

<template>
  <v-stepper v-model="currentStep">
    <v-stepper-header>
      <v-stepper-item
        v-for="(step, index) in steps"
        :key="index"
        :title="step.title"
        :value="step.value"
        :subtitle="step.subtitle"
        :complete="step.complete"
      ></v-stepper-item>
    </v-stepper-header>
    <v-stepper-content
      v-for="(step, index) in steps"
      :key="index"
      :step="step.value"
    >
      <v-sheet v-if="step.value === currentStep">
        <v-card-text>
          <template v-if="step.value === 1">
            <h2>Select a collection to edit, delete, or create a new one</h2>
            <v-responsive>
              <div class="flex-shrink-0">
                <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  label="Search"
                  variant="underlined"
                  class="input-field"
                  style="max-width: 478px"
                ></v-text-field>
              </div>
            </v-responsive>
            <v-data-table
              :headers="collectionHeaders"
              :items="collectionsArray"
              :search="search"
              item-key="id"
              show-select
              v-model="selectedCollection"
              class="elevation-2"
              show-expand
              select-strategy="single"
              :items-per-page="25"
              :items-per-page-options="itemsPerPage"
              no-data-text="No collections available"
            >
              <template v-slot:expanded-row="{ columns, item }">
                <tr>
                  <td :colspan="columns.length">
                    <table class="expanded-row">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Description</th>
                          <th>Type</th>
                          <th>Scope</th>
                        </tr>
                      </thead>
                      <tbody>
                        <template
                          v-for="(categoryItems, categoryKey) in item.items"
                          :key="categoryKey"
                        >
                          <tr
                            v-for="subItem in categoryItems"
                            :key="subItem.name"
                          >
                            <td>{{ subItem.name }}</td>
                            <td>{{ subItem.description }}</td>
                            <td>{{ categoryKey }}</td>
                            <td>{{ subItem.scope }}</td>
                          </tr>
                        </template>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </template>
            </v-data-table></template
          >
          <template v-if="step.value === 2">
            <h2>Editing Collection - {{ collectionObj.collectionName }}</h2>
            <v-responsive>
              <div class="d-flex">
                <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  label="Search"
                  variant="underlined"
                  class="input-field"
                  style="margin-right: 400px"
                ></v-text-field>
                <v-select
                  v-model="selectedType"
                  :items="typeOptions"
                  chips
                  multiple
                  label="Type"
                  variant="underlined"
                  class="input-field"
                  style="margin-right: 20px"
                ></v-select>
                <v-select
                  v-model="selectedScope"
                  :items="scopeOptions"
                  chips
                  multiple
                  label="Scope"
                  variant="underlined"
                  class="input-field"
                ></v-select>
              </div>
            </v-responsive>
            <v-data-table
              :headers="itemHeaders"
              :items="filteredItems"
              :search="search"
              item-key="id"
              show-select
              v-model="selected"
              class="elevation-2"
              show-expand
              :items-per-page="25"
              :items-per-page-options="itemsPerPage"
            >
              <template v-slot:expanded-row="{ columns, item }">
                <tr>
                  <td :colspan="columns.length">
                    <table class="expanded-row">
                      <thead>
                        <tr>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            {{
                              item.description
                                ? item.description
                                : "No Description"
                            }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </template>
          <template v-if="step.value === 3">
            <h2>Review your changes before submission</h2>
            <div style="margin-bottom: 10px"></div>
            <v-data-table
              :headers="deployHeaders"
              :items="processSelectedItems()"
              item-key="id"
              :items-per-page="-1"
              :items-per-page-options="[-1]"
              class="elevation-2"
            >
              <template #[`item.status`]="{ item }">
                <div style="display: flex; justify-content: left">
                  <v-chip :color="changeStatusColour(item.status)">{{
                    item.status
                  }}</v-chip>
                </div>
              </template>
            </v-data-table>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-btn
            v-if="selectedCollection.length > 0 && currentStep == 1"
            variant="elevated"
            color="primary"
            @click="deleteDialogBox(true)"
            class="mr-auto"
            >Delete Collection</v-btn
          >
          <v-btn
            v-if="selectedCollection.length == 0 && currentStep == 1"
            variant="elevated"
            color="primary"
            @click="createCollectionDialogBox(true)"
            class="mr-auto"
            >Create New Collection</v-btn
          >
          <v-btn
            v-if="currentStep > 1"
            variant="elevated"
            @click="prevStep"
            class="mr-auto"
            color="rgb(247, 247, 247)"
            >Back</v-btn
          >
          <v-btn
            v-if="currentStep < 3"
            variant="elevated"
            color="primary"
            @click="nextStep"
            >Next</v-btn
          >
          <v-btn
            v-if="currentStep == 3"
            @click="submissionDialogBox(true)"
            variant="elevated"
            color="primary"
            >Submit Collection</v-btn
          >
        </v-card-actions>
      </v-sheet>
      <v-dialog v-model="showCreationDialog" persistent>
        <v-card min-width="600" variant="elevated" class="mx-auto">
          <v-card-title>Enter the new Collection details</v-card-title>
          <v-card-text>
            <v-table class="text-left">
              <v-responsive class="mx-auto py-2" max-width="600">
                <v-text-field
                  clearable
                  v-model="collectionObj.collectionName"
                  label="Collection Name"
                  type="input"
                  hint="Enter the new collection name"
                  variant="outlined"
                ></v-text-field>
              </v-responsive>
              <v-responsive class="mx-auto py-2" max-width="600">
                <v-text-field
                  clearable
                  v-model="collectionObj.collectionDescription"
                  label="Description"
                  type="input"
                  hint="Enter a description for the collection"
                  variant="outlined"
                ></v-text-field>
              </v-responsive>
              <div class="text-center">
                <v-card>
                  <v-divider></v-divider>
                  <v-card-actions>
                    <v-btn @click="creationDialogBox(false)">Cancel</v-btn>
                    <v-btn
                      @click="createNewCollection"
                      class="flex-grow-1"
                      variant="tonal"
                      :disabled="
                        !collectionObj.collectionName ||
                        !collectionObj.collectionDescription
                      "
                      >Create</v-btn
                    >
                  </v-card-actions>
                </v-card>
              </div>
            </v-table>
          </v-card-text>
        </v-card>
      </v-dialog>
      <v-dialog v-model="showWarningDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <v-card-text
              ><h3>Please select an item before proceeding</h3></v-card-text
            >

            <v-divider></v-divider>
            <v-card-actions>
              <v-btn
                class="flex-grow-1"
                variant="tonal"
                @click="selectionWarningDialogBox"
                >OK</v-btn
              ></v-card-actions
            >
          </v-card>
        </div>
      </v-dialog>
      <v-dialog v-model="showDeleteDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <v-card-text>
              <h3>You are about to delete this collection, please confirm</h3>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-btn @click="deleteDialogBox(false)">Cancel</v-btn>
              <v-btn
                @click="deleteCollection(selectedCollection)"
                class="flex-grow-1"
                variant="tonal"
                >Delete</v-btn
              >
            </v-card-actions>
          </v-card>
        </div>
      </v-dialog>
      <v-dialog v-model="showDeleteSuccessDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <v-card-text><h3>Collection has been deleted</h3></v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-btn
                class="flex-grow-1"
                variant="tonal"
                @click="deleteSuccessDialogBox(false)"
                >OK</v-btn
              >
            </v-card-actions>
          </v-card>
        </div>
      </v-dialog>
      <v-dialog v-model="showDeleteFailedDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <v-card-text
              ><h3>Collection has failed to be deleted</h3></v-card-text
            >
            <v-divider></v-divider>
            <v-card-actions>
              <v-btn
                class="flex-grow-1"
                variant="tonal"
                @click="deleteFailedDialogBox(false)"
                >OK</v-btn
              >
            </v-card-actions>
          </v-card>
        </div>
      </v-dialog>
      <v-dialog v-model="showCompletionDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <div class="py-12 text-center">
              <v-icon
                class="mb-6"
                color="success"
                icon="mdi-check-circle-outline"
                size="128"
              ></v-icon>
              <div class="text-h4 font-weight-bold">Collection Submitted</div>
            </div>
            <v-divider></v-divider>
            <div class="pa-4 text-center">
              <v-btn
                color="medium-emphasis"
                min-width="200"
                variant="outlined"
                @click="resetWizard"
              >
                Close
              </v-btn>
            </div>
          </v-card>
        </div>
      </v-dialog>
      <v-dialog v-model="showSubmissionDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <v-card-text
              ><h3>
                You are about to submit the selected items into the targeted
                collection. Are you sure you want to proceed?
              </h3>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-btn @click="submissionDialogBox">Cancel</v-btn>
              <v-btn
                @click="submitCollection"
                class="flex-grow-1"
                variant="tonal"
                >Proceed</v-btn
              >
            </v-card-actions>
          </v-card>
        </div>
      </v-dialog>
      <v-dialog
        v-model="showCompletionWarningDialog"
        max-width="500"
        persistent
      >
        <div class="text-center">
          <v-card>
            <div class="py-12 text-center">
              <v-icon
                class="mb-6"
                color="red"
                icon="mdi-alert-circle-outline"
                size="128"
              ></v-icon>
              <div class="text-h4 font-weight-bold">
                Error Submitting Collection
              </div>
            </div>
            <div class="pa-4 text-center">
              <v-btn
                color="medium-emphasis"
                min-width="200"
                variant="outlined"
                @click="completionWarningDialogBox"
              >
                Close
              </v-btn>
            </div>
          </v-card>
        </div>
      </v-dialog>
      <v-dialog v-model="loading" persistent width="auto">
        <v-card color="primary">
          <v-card-text>
            Loading data, please wait...
            <v-progress-linear indeterminate></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-stepper-content>
  </v-stepper>
</template>

<style scoped>
/* Required for the scope filter to work correctly. */
.input-field {
  width: 50%;
}
/* tbody tr:nth-of-type(odd) {
  background-color: rgb(247, 247, 247);
} */

/* tbody tr:nth-of-type(even) {
  background-color: ;
} */
.expanded-row {
  width: 100%;
  border-collapse: collapse;
}
.expanded-row th {
  background-color: rgb(210, 210, 210);
  border: 1px solid #a4a4a4;
}
.expanded-row td {
  padding: 1px;
  border: 1px solid #a4a4a4;
  /* background-color: rgb(247, 247, 247); */
}
/* Control the width of the columns in the expanded row. */
.expanded-row td:nth-child(1) {
  width: 20%;
}
.expanded-row td:nth-child(2) {
  width: 60%;
}
.expanded-row td:nth-child(3) {
  width: 10%;
  text-align: center;
}
.expanded-row td:nth-child(4) {
  width: 10%;
  text-align: center;
}
</style>
